import { get, isArray, isEqual, isNull, isObject, isString, pick } from 'lodash-es';
import { useObservable } from 'observable-hooks';
import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
    defer,
    distinctUntilChanged,
    fromEventPattern,
    identity,
    map,
    startWith,
    switchMap,
} from 'rxjs';

import testSome from '../../utils/testSome';
import { useDeepCompareMemoize } from '../../utils/use-deep-compare-memoize';

export const OVERRIDES_NAME = 'override';

// This is our own form context. Not the `useFormContext` from react-form-hook.
export const FormContext = createContext();

export const FormFieldContext = createContext(null);

export const defaultPropTypes = {
    fieldOptions: PropTypes.shape({
        name: PropTypes.string.isRequired,
        settings: PropTypes.object,
    }).isRequired,
};

export const useIsOverridable = (name) => {
    const { parentValues, projectIgnoreFields } = useContext(FormContext);

    // Not overridable when there is no parentValues loaded.
    if (isNull(parentValues)) {
        return false;
    }

    // Not overridable when this field is in the ignore list.
    return !(projectIgnoreFields && testSome(projectIgnoreFields, name));
};

export const useIsOverridden = (name) => {
    const { watch, getValues } = useFormContext();
    const getValue = useCallback(() => getValues(`${OVERRIDES_NAME}.${name}`), [name, getValues]);

    const [overridden, setOverridden] = useState(getValue() || false);

    useEffect(() => {
        const subscription = watch((_values, { name: nameOfUpdated }) => {
            if (nameOfUpdated === `${OVERRIDES_NAME}.${name}`) {
                setOverridden(getValue);
            }
        });
        return subscription.unsubscribe;
    }, [watch, getValue, name]);

    return overridden;
};

/**
 * Converts a structure like:
 * {
 *     logFlushInterval: false,
 *     dustSampleTime: true,
 *     notificationConfig: {
 *         leqAlarms: false,
 *         emailDelay: true,
 *     },
 * }
 * Into an array of enabled field paths:
 * [
 *     'dustSampleTime',
 *     'notificationConfig.emailDelay'
 * ]
 */
export function extractEnabledFields(fields, parentPath = '') {
    let entries = [];

    if (fields instanceof Map) {
        entries = Array.from(fields);
    } else if (isObject(fields)) {
        entries = Object.entries(fields);
    }

    return entries.flatMap(([key, value]) => {
        const currentPath = parentPath ? `${parentPath}.${key}` : key;

        if (key === 'notificationConfig') {
            // Recursively process nested objects.
            return extractEnabledFields(value, currentPath);
        }

        // Return the path if the value is truthy, otherwise empty array.
        return value ? [currentPath] : [];
    });
}

export const useWatchNestedField = (watch, field, defaultValue) => {
    const [fieldValue, setFieldValue] = useState(defaultValue);

    useEffect(() => {
        const subscription = watch((values, { name }) => {
            if (name && name.startsWith(field)) {
                setFieldValue(() => ({ ...values[field] }));
            }
        });
        return subscription.unsubscribe;
    }, [watch, field]);

    return fieldValue;
};

// Disabling `no-underscore-dangle` as we are intentionally accessing internal
// React Hook Form properties for state snapshotting, which is not something we control.
/* eslint-disable no-underscore-dangle */

/**
 * Creates an observable from React Hook Form's internal `formControl` object.
 * This is an advanced utility for observing the entire form state or a subset of it.
 *
 * It correctly provides an initial emission of the current state upon subscription,
 * followed by subsequent updates, and includes optimized `distinctUntilChanged` logic
 * to prevent unnecessary emissions.
 *
 * @param {object} formControl The `formControl` object from React Hook Form's `useFormContext`.
 * @param {string|string[]|null} [property] The specific form state property or properties to
 * observe (e.g., 'isDirty', or ['isDirty', 'isValid']). If omitted, the whole FormState is
 * returned.
 * @returns {Observable<any>} An observable of the form state or a specific property/subset of it.
 */
export function fromReactHookFormControl(formControl, property, lazy = false) {
    // `defer` ensures the entire setup logic is executed lazily for each new subscriber,
    // making sure the `initialState` is fresh at the moment of subscription.
    return defer(() => {
        // The initial state is captured at the moment of subscription.
        const initialState = {
            values: { ...formControl.control._formValues },
            ...formControl.control._formState,
        };

        const fieldsToWatch = Object.fromEntries(
            (isArray(property) ? property : [property]).map((p) => [p, true])
        );

        const changes$ = fromEventPattern(
            (handler) => formControl.subscribe({ formState: fieldsToWatch, callback: handler }),
            (_handler, unsubscribe) => unsubscribe
        );

        // Create the final observable by piping a chain of operators.
        return changes$.pipe(
            // `startWith` prepends the initial state to the stream, ensuring an immediate emission.
            lazy ? identity : startWith(initialState),
            // This `map` operator is responsible for shaping the data into its final desired form
            // based on the `property` argument.
            map((formState) => {
                if (isString(property)) {
                    // If `property` is a string, pluck that single value from the form state.
                    return get(formState, property);
                }
                if (isArray(property)) {
                    // If `property` is an array, return an object containing only those keys.
                    return pick(formState, property);
                }
                // If `property` is null or undefined, return the whole state object.
                return formState;
            }),
            // This `distinctUntilChanged` operator prevents duplicate emissions. It performs a
            // deep equality check on the value it receives from the `map` operator above.
            // We do not apply this for the `errors` object, as its reference in React Hook Form
            // is often reused, which could prevent legitimate updates from being emitted.
            property === 'errors' ? identity : distinctUntilChanged(isEqual)
        );
    });
}
/* eslint-enable no-underscore-dangle */

/**
 * Creates an efficient, memoized observable for a specific property or subset
 * of React Hook Form's `formState`.
 *
 * @param {string|string[]|null} [property] The specific form state property or properties to
 * observe (e.g., 'isDirty', or ['isDirty', 'isValid']). If omitted, the entire form state is
 * returned.
 * @returns {Observable} An observable that emits the requested part of the form state whenever
 * it changes.
 */
export function useFormStateObservable(property) {
    const { formControl } = useFormContext();

    // The `property` array/object can be a new reference on every render, which
    // would cause `useObservable` to resubscribe unnecessarily. `useDeepCompareMemoize`
    // provides a referentially stable version of `property` as long as its contents
    // do not change deeply.
    const stableProperty = useDeepCompareMemoize(property);

    // `useObservable` creates and subscribes to our observable stream.
    // All the complex logic for watching form state is neatly encapsulated in
    // `fromReactHookFormControl`. The stream is only re-created if `formControl`
    // or the content of `property` (via `stableProperty`) changes.
    return useObservable(
        (inputs$) => inputs$.pipe(switchMap(([fc, prop]) => fromReactHookFormControl(fc, prop))),
        // Use the stabilized property as a dependency to prevent unnecessary re-subscriptions.
        [formControl, stableProperty]
    );
}

function fromReactHookFormWatch(formControl, field = null, lazy = true) {
    return fromReactHookFormControl(formControl, 'values', lazy).pipe(
        map((value) => {
            if (field == null) {
                return value;
            }
            if (Array.isArray(field)) {
                return pick(value, field);
            }
            return get(value, field);
        })
    );
}

/**
 * Custom hook to create an observable for React Hook Form's `watch` functionality.
 * @param {string | string[] | null} [field=null] The field name(s) to watch.
 * Pass null or omit to watch all fields.
 * @param {boolean} [lazy=true] Determines the initial emission behavior.
 * If `true` (default), the observable only emits when form values change after subscription.
 * If `false`, the observable immediately emits the current form values (using `getValues()`)
 * upon subscription, followed by subsequent changes.
 * @returns {Observable<any>} An observable of the watched form values.
 */
export function useWatchObservable(field = null, lazy = true) {
    const { formControl } = useFormContext();

    return useObservable(
        (inputs$) =>
            inputs$.pipe(
                switchMap(([fc]) => fromReactHookFormWatch(fc, field, lazy)),
                distinctUntilChanged()
            ),
        [formControl]
    );
}
